/**
 * battle-template-apply.ts — v0M4.3.3 (2026-07-23).
 *
 * Converts a TerrainTemplate (raw 20×20 string grid from BattleMap.xlsx)
 * into a ready-to-use battlescape terrain + features + wallCells triple.
 *
 * Token → TerrainType mapping (per Michael's xlsx legend + sim reuse):
 *   X   → base cell (either strategic-cue Grass/Hills/etc. or Grass fallback)
 *   O   → Grass (city interior — walkable, no combat effect at v1)
 *   OO  → Fortification (wall; HP tracked via wallCells output)
 *   OOO → Grass (gate — walkable, visually distinct via a TerrainFeature)
 *   =   → Water (river; impassable to non-cavalry per current terrain rules)
 *   |   → Bridge (walkable, spans river)
 *
 * Called by `battle-factory.ts` for both siege battles (v0M4.3.3 replaces
 * `generateSiegeBattleTerrain`) and field battles where a matching river
 * template exists (v0M4.3.3 layers on top of the strategic-cue generator).
 */

import type { RngState } from './rng';
import { nextInt } from './rng';
import { BATTLESCAPE_HEIGHT, BATTLESCAPE_WIDTH, BATTLESCAPE_CELL_COUNT, TERRAIN_TYPE_ORDER } from './battle-state';
import type { TerrainFeature, TerrainType } from './battle-state';
import type { TerrainTemplate, TerrainTemplates } from '../data/battle-templates';
import { templatesByCategory } from '../data/battle-templates';

/**
 * v0M13.2b (2026-07-30) — height of one half-template. Two halves stitched
 * together fill the full 20×20 battlescape.
 */
const HALF_HEIGHT = BATTLESCAPE_HEIGHT / 2; // 10 with BATTLESCAPE_HEIGHT=20

/** Result of applying a terrain template. Same shape as GeneratedSiegeTerrain. */
export interface AppliedTemplate {
  readonly terrain: Uint8Array;
  readonly features: TerrainFeature[];
  readonly wallCells: readonly number[];
}

function ordinalOf(t: TerrainType): number {
  const idx = TERRAIN_TYPE_ORDER.indexOf(t);
  if (idx < 0) throw new Error(`Unknown terrain type '${t}'`);
  return idx;
}

/**
 * Apply a terrain template on top of an optional base terrain. Cells
 * containing `X` (or empty) preserve whatever the base grid holds so
 * strategic-cue variation shows through in river-crossing templates.
 * Non-`X` tokens overwrite the base cell.
 */
export function applyTerrainTemplate(
  template: TerrainTemplate,
  base?: Uint8Array,
): AppliedTemplate {
  const cellCount = template.grid.length * template.grid[0]!.length;
  const terrain = base ? new Uint8Array(base) : new Uint8Array(cellCount);
  const wallCells: number[] = [];
  const gateCells: [number, number][] = [];
  const cityCells: [number, number][] = [];
  const bridgeCells: [number, number][] = [];
  const riverCells: [number, number][] = [];

  for (let r = 0; r < template.grid.length; r++) {
    const row = template.grid[r]!;
    for (let c = 0; c < row.length; c++) {
      const token = row[c]!.trim();
      const idx = r * BATTLESCAPE_WIDTH + c;
      if (!token || token === 'X') {
        // Preserve base cell (falls back to Grass = ordinal 0 when no base).
        continue;
      }
      if (token === 'O') {
        // Settlement interior — walkable Grass for sim; visual overlay via feature.
        terrain[idx] = ordinalOf('Plains');
        cityCells.push([c, r]);
      } else if (token === 'OO') {
        terrain[idx] = ordinalOf('Wall');
        wallCells.push(idx);
      } else if (token === 'OOO') {
        // v0M4.0k.2 (2026-07-27) — Gate cells route to the Gate terrain
        // type (distinct from Plains). Enemy cohorts can enter Gate
        // cells; Wall cells reject enemy movement. Combat matrix
        // lookups target Gate as a distinct row/col per Combat.xlsx.
        terrain[idx] = ordinalOf('Gate');
        gateCells.push([c, r]);
      } else if (token === '=') {
        terrain[idx] = ordinalOf('Water');
        riverCells.push([c, r]);
      } else if (token === '|') {
        terrain[idx] = ordinalOf('Bridge');
        bridgeCells.push([c, r]);
      }
      // Unknown tokens are silently preserved-as-base; xlsx author error.
    }
  }

  const features: TerrainFeature[] = [];
  if (cityCells.length > 0) {
    features.push({ type: 'Wall', cells: cityCells });
    // Reusing 'Wall' feature type for city-interior tiles so
    // renderer paints them distinctly. Semantically overloaded; a
    // dedicated 'CityTile' feature type is a post-MVP polish item.
  }
  if (gateCells.length > 0) {
    features.push({ type: 'Gate', cells: gateCells });
    // v0M4.0k.2 — Gate feature type (renamed from reused 'Bridge').
    // Renderer picks Gate glyph (arched opening); combat matrix
    // targets 'gate' row/col.
  }
  if (bridgeCells.length > 0) {
    features.push({ type: 'Bridge', cells: bridgeCells });
  }
  if (riverCells.length > 0) {
    features.push({ type: 'RiverStrip', cells: riverCells });
  }

  return { terrain, features, wallCells };
}

/**
 * Category-based template picker. Returns null when no template matches
 * the requested category — caller falls back to procedural generation.
 *
 * When multiple templates share a category (e.g. 3 RiverCrossing
 * variants), one is RNG-picked using the passed `rng` state.
 */
export function pickTerrainTemplate(
  templates: TerrainTemplates,
  category: 'settlement_walled' | 'settlement_nowalls' | 'river' | 'fort_walled',
  rng: RngState,
): TerrainTemplate | null {
  const candidates = templatesByCategory(templates, category);
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0]!;
  const idx = nextInt(rng, 0, candidates.length);
  return candidates[idx]!;
}

/**
 * v0M13.2b (2026-07-30) — pair-picker for half-template categories.
 *
 * Picks TWO templates independently from the category pool, one per side.
 * Returns null when the pool is empty. Both picks come from the same
 * pool so any category-specific invariant (e.g. all river-category
 * halves contain a river band) is preserved on both halves of the
 * stitched battlescape.
 *
 * The two picks are independent — the same source template can appear
 * as both top and bot. That's fine: for a river template split into
 * `{name}_top` + `{name}_bot`, picking both halves recomposes the
 * original 20×20 layout; picking two different sources gives a mixed
 * battlescape. Runtime `stitchAndApplyHalves` handles the mirror-top /
 * unmirror-bottom placement.
 */
export function pickTerrainTemplatePair(
  templates: TerrainTemplates,
  category: 'settlement_walled' | 'settlement_nowalls' | 'river' | 'fort_walled',
  rng: RngState,
): { top: TerrainTemplate; bottom: TerrainTemplate } | null {
  const candidates = templatesByCategory(templates, category);
  if (candidates.length === 0) return null;
  const topIdx = candidates.length === 1 ? 0 : nextInt(rng, 0, candidates.length);
  const botIdx = candidates.length === 1 ? 0 : nextInt(rng, 0, candidates.length);
  return { top: candidates[topIdx]!, bottom: candidates[botIdx]! };
}

/**
 * v0M13.2b (2026-07-30) — stitch two 20×10 half-templates into a full
 * 20×20 battlescape per `BATTLES.md` §2.8.6-B:
 *
 *  - **Bottom half (unmirrored):** authored row `r` → final row `r + 10`.
 *    This is the "player" side by convention (side 0 in battle-factory's
 *    lower-faction-id → south rule).
 *  - **Top half (mirrored vertically):** authored row `r` → final row
 *    `(HALF_HEIGHT - 1) - r`. This flips the top-half template so its
 *    "front edge" (row 0 in the authored orientation, i.e. closest to
 *    the enemy) lands at the middle line (row 9 of the final grid) —
 *    facing the bottom half's front edge across the middle.
 *
 * Both templates must be exactly HALF_HEIGHT rows tall; a mismatch is a
 * bug in the xlsx pipeline or a caller that passed a full-size siege
 * template — throw loud rather than silently truncate.
 *
 * The `base` parameter behaves like `applyTerrainTemplate`: cells with
 * `X` or empty tokens preserve the base terrain (letting strategic-cue
 * variation show through outside the authored features). If no base is
 * supplied, cells default to Plains (ordinal 0).
 *
 * Feature attribution is computed in the final-grid coordinate space so
 * downstream consumers (renderer, wall-breach system, etc.) can treat
 * the result identically to a full-size template.
 */
export function stitchAndApplyHalves(
  top: TerrainTemplate,
  bottom: TerrainTemplate,
  base?: Uint8Array,
): AppliedTemplate {
  if (top.grid.length !== HALF_HEIGHT) {
    throw new Error(
      `stitchAndApplyHalves: top template '${top.name}' has ${top.grid.length} rows, expected ${HALF_HEIGHT}`,
    );
  }
  if (bottom.grid.length !== HALF_HEIGHT) {
    throw new Error(
      `stitchAndApplyHalves: bottom template '${bottom.name}' has ${bottom.grid.length} rows, expected ${HALF_HEIGHT}`,
    );
  }

  const terrain = base ? new Uint8Array(base) : new Uint8Array(BATTLESCAPE_CELL_COUNT);
  const wallCells: number[] = [];
  const gateCells: [number, number][] = [];
  const cityCells: [number, number][] = [];
  const bridgeCells: [number, number][] = [];
  const riverCells: [number, number][] = [];

  // Bottom half — unmirrored, rows 10-19.
  for (let r = 0; r < HALF_HEIGHT; r++) {
    const row = bottom.grid[r]!;
    const finalRow = r + HALF_HEIGHT;
    for (let c = 0; c < row.length; c++) {
      writeTokenAt(terrain, row[c]!, finalRow, c, wallCells, gateCells, cityCells, bridgeCells, riverCells);
    }
  }
  // Top half — mirrored vertically, rows 0-9. Authored row r → final row (9 - r).
  for (let r = 0; r < HALF_HEIGHT; r++) {
    const row = top.grid[r]!;
    const finalRow = (HALF_HEIGHT - 1) - r;
    for (let c = 0; c < row.length; c++) {
      writeTokenAt(terrain, row[c]!, finalRow, c, wallCells, gateCells, cityCells, bridgeCells, riverCells);
    }
  }

  const features: TerrainFeature[] = [];
  if (cityCells.length > 0) features.push({ type: 'Wall', cells: cityCells });
  if (gateCells.length > 0) features.push({ type: 'Gate', cells: gateCells });
  if (bridgeCells.length > 0) features.push({ type: 'Bridge', cells: bridgeCells });
  if (riverCells.length > 0) features.push({ type: 'RiverStrip', cells: riverCells });

  return { terrain, features, wallCells };
}

/**
 * Internal helper — shared between `applyTerrainTemplate` and
 * `stitchAndApplyHalves`. Writes a single token at (finalRow, c) into
 * terrain + the appropriate feature-collection accumulator.
 */
function writeTokenAt(
  terrain: Uint8Array,
  rawToken: string,
  finalRow: number,
  c: number,
  wallCells: number[],
  gateCells: [number, number][],
  cityCells: [number, number][],
  bridgeCells: [number, number][],
  riverCells: [number, number][],
): void {
  const token = rawToken.trim();
  if (!token || token === 'X') return; // Preserve base.
  const idx = finalRow * BATTLESCAPE_WIDTH + c;
  if (token === 'O') {
    terrain[idx] = ordinalOf('Plains');
    cityCells.push([c, finalRow]);
  } else if (token === 'OO') {
    terrain[idx] = ordinalOf('Wall');
    wallCells.push(idx);
  } else if (token === 'OOO') {
    terrain[idx] = ordinalOf('Gate');
    gateCells.push([c, finalRow]);
  } else if (token === '=') {
    terrain[idx] = ordinalOf('Water');
    riverCells.push([c, finalRow]);
  } else if (token === '|') {
    terrain[idx] = ordinalOf('Bridge');
    bridgeCells.push([c, finalRow]);
  }
  // Unknown tokens: preserved-as-base (xlsx author error).
}
